import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { GraduationCap, Users, CheckCircle2, AlertTriangle, Clock, FileEdit } from 'lucide-react';
import { api } from '@/lib/api';

interface DashboardData {
  totalPackages: number;
  activePackages: number;
  totalAssignments: number;
  completedAssignments: number;
  pendingAssignments: number;
  overdueAssignments: number;
  completionRate: number;
  averageCompletionHours: number | null;
  byRole: { role: string; total: number; completed: number; overdue: number }[];
}

export default function TrainingDashboardPage() {
  const { data: dashData, isLoading } = useQuery({
    queryKey: ['training-dashboard'],
    queryFn: () => api.training.dashboard(),
    staleTime: 60_000,
  });

  const { data: draftsData } = useQuery({
    queryKey: ['training-drafts'],
    queryFn: () => api.training.listPackages('draft'),
    staleTime: 60_000,
  });

  const { data: publishedData } = useQuery({
    queryKey: ['training-published'],
    queryFn: () => api.training.listPackages('published'),
    staleTime: 60_000,
  });

  const dash = (dashData?.data ?? {}) as unknown as DashboardData;
  const drafts = (draftsData?.data ?? []) as Record<string, unknown>[];
  const published = (publishedData?.data ?? []) as Record<string, unknown>[];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <GraduationCap className="h-7 w-7 text-teal-400" />
        <div>
          <h1 className="text-2xl font-bold">Training Dashboard</h1>
          <p className="text-sm text-muted-foreground">Completion tracking and training evidence</p>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-4 gap-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <StatCard label="Published Packages" value={dash.activePackages ?? 0} icon={FileEdit} />
            <StatCard label="Completion Rate" value={`${dash.completionRate ?? 0}%`} icon={CheckCircle2} color="text-compliance-low" />
            <StatCard label="Pending" value={dash.pendingAssignments ?? 0} icon={Clock} color="text-yellow-500" />
            <StatCard label="Overdue" value={dash.overdueAssignments ?? 0} icon={AlertTriangle} color="text-compliance-critical" />
          </div>

          {drafts.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold mb-2">Drafts Awaiting Review ({drafts.length})</h2>
              <div className="space-y-2">
                {drafts.map((pkg) => (
                  <Link key={pkg.id as string} to={`/training/packages/${pkg.id}`}
                    className="block rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 hover:bg-yellow-500/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{pkg.title as string}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          AI confidence: {Math.round(((pkg.aiConfidence as number) ?? 0) * 100)}% —
                          {((pkg.aiConfidence as number) ?? 0) < 0.8 ? ' needs review' : ' auto-publish eligible'}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">Review →</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="text-sm font-semibold mb-2">By Role</h2>
            {(dash.byRole ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No assignments yet</p>
            ) : (
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-4 py-2 text-left">Role</th>
                      <th className="px-4 py-2 text-right">Total</th>
                      <th className="px-4 py-2 text-right">Completed</th>
                      <th className="px-4 py-2 text-right">Overdue</th>
                      <th className="px-4 py-2 text-right">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dash.byRole ?? []).map((r) => (
                      <tr key={r.role} className="border-t border-border">
                        <td className="px-4 py-2">{r.role}</td>
                        <td className="px-4 py-2 text-right">{r.total}</td>
                        <td className="px-4 py-2 text-right text-compliance-low">{r.completed}</td>
                        <td className="px-4 py-2 text-right text-compliance-critical">{r.overdue}</td>
                        <td className="px-4 py-2 text-right">
                          {r.total > 0 ? Math.round((r.completed / r.total) * 100) : 0}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {published.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold mb-2">Published Training ({published.length})</h2>
              <div className="space-y-2">
                {published.map((pkg) => (
                  <Link key={pkg.id as string} to={`/training/packages/${pkg.id}`}
                    className="block rounded-lg border border-border bg-card p-3 hover:bg-muted/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{pkg.title as string}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {((pkg.rolesAffected as string[]) ?? []).join(', ')}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">View →</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = 'text-teal-400' }: {
  readonly label: string; readonly value: string | number; readonly icon: typeof Users; readonly color?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-bold">{value}</p>
        </div>
        <Icon className={`h-6 w-6 ${color} opacity-60`} />
      </div>
    </div>
  );
}
